/* VDB Fitment 1.7.5 — paginated full-list product-page + product-card saved-vehicle fitment. */
(() => {
  'use strict';

  const BUILD = '1.7.5';
  const CATALOG = 'UK26';
  const VERSION_RE = /^UK26-V-\d{6}$/;
  const defaultShop = () => (window.Shopify && window.Shopify.shop) || location.host;
  const storageKey = shop => `vdb:vehicle:${CATALOG}:${shop}`;
  window.VDBFitmentBuild = BUILD;

  // If the customer starts changing the visible vehicle selector, the old saved
  // fitment result must not remain visible beside the new draft selection. A
  // fitment result is only authoritative after the vehicle has been saved.
  const dirtyShops = new Set();

  function shopForNode(node) {
    const lookup = node && node.closest ? node.closest('vdb-mmy') : null;
    return (lookup && lookup.dataset.shop) || defaultShop();
  }

  function setDraftDirty(shop, dirty) {
    const key = shop || defaultShop();
    if (dirty) dirtyShops.add(key); else dirtyShops.delete(key);
    document.querySelectorAll('vdb-fitment-status, vdb-card-fitment').forEach(root => {
      const rootShop = root.dataset.shop || defaultShop();
      if (rootShop !== key) return;
      if (dirty) {
        root.hidden = true;
        root.setAttribute('aria-hidden', 'true');
        root.dataset.vdbDraftDirty = 'true';
      } else {
        delete root.dataset.vdbDraftDirty;
      }
    });
  }

  function isDraftDirty(shop) {
    return dirtyShops.has(shop || defaultShop());
  }

  function normalize(candidate) {
    if (!candidate || typeof candidate !== 'object') return null;
    const rawIds = Array.isArray(candidate.versionIds)
      ? candidate.versionIds
      : (candidate.versionId ? [candidate.versionId] : []);
    const versionIds = [...new Set(rawIds.map(String).filter(id => VERSION_RE.test(id)))];
    if (!versionIds.length) return null;
    return {
      ...candidate,
      catalog: candidate.catalog || CATALOG,
      versionIds,
      versionId: versionIds.length === 1 ? versionIds[0] : (candidate.versionId || null)
    };
  }

  function selectionFromVisibleLookup(shop) {
    const lookups = [...document.querySelectorAll('vdb-mmy')];
    const ordered = [
      ...lookups.filter(el => !shop || !el.dataset.shop || el.dataset.shop === shop),
      ...lookups.filter(el => shop && el.dataset.shop && el.dataset.shop !== shop)
    ];

    for (const lookup of ordered) {
      const direct = normalize(lookup.saved);
      if (direct) return direct;

      // If the lookup has restored a saved vehicle but its public property has not
      // yet been assigned, its current stored selection is still available here.
      try {
        if (window.VDBVehicle && typeof window.VDBVehicle.getSelection === 'function') {
          const viaLookupShop = normalize(window.VDBVehicle.getSelection(lookup.dataset.shop || shop || defaultShop()));
          if (viaLookupShop) return viaLookupShop;
        }
      } catch (_) { /* continue */ }
    }
    return null;
  }

  function selectionFromApi(shop) {
    try {
      if (window.VDBVehicle && typeof window.VDBVehicle.getSelection === 'function') {
        return normalize(window.VDBVehicle.getSelection(shop || defaultShop()));
      }
    } catch (_) { /* continue to storage */ }
    return null;
  }

  function selectionFromStorage(shop) {
    try {
      const preferred = [shop, defaultShop(), location.host].filter(Boolean);
      for (const s of [...new Set(preferred)]) {
        const raw = localStorage.getItem(storageKey(s));
        if (!raw) continue;
        try {
          const value = normalize(JSON.parse(raw));
          if (value) return value;
        } catch (_) { /* continue */ }
      }

      for (let i = 0; i < localStorage.length; i += 1) {
        const key = localStorage.key(i) || '';
        if (!key.startsWith(`vdb:vehicle:${CATALOG}:`)) continue;
        const raw = localStorage.getItem(key);
        if (!raw) continue;
        try {
          const value = normalize(JSON.parse(raw));
          if (value) return value;
        } catch (_) { /* continue */ }
      }
    } catch (_) { /* localStorage unavailable */ }
    return null;
  }

  function getSelection(shop) {
    // The on-page lookup is authoritative because it is the component the customer
    // can actually see as saved. API and storage are fallbacks for pages/layouts
    // where that lookup is not present.
    return selectionFromVisibleLookup(shop) || selectionFromApi(shop) || selectionFromStorage(shop);
  }

  function idsFromNode(node) {
    if (!node) return new Set();
    // Accept both the legacy JSON-array payload and the 1.7.4 complete text
    // payload. Extracting public VDB IDs directly avoids Shopify Liquid's
    // 50-resource normal for-loop ceiling.
    const values = String(node.textContent || '').match(/UK26-V-\d{6}/g) || [];
    return new Set(values.filter(value => VERSION_RE.test(value)));
  }

  function mergeIds(target, source) {
    source.forEach(value => target.add(value));
    return target;
  }

  function nextUrlFrom(root, attr) {
    const node = root && root.querySelector ? root.querySelector(`[${attr}]`) : null;
    return node ? String(node.getAttribute(attr) || '').trim() : '';
  }

  async function loadPaginatedIds(root, idSelector, nextAttr, rootSelector) {
    const ids = idsFromNode(root.querySelector(idSelector));
    let next = nextUrlFrom(root, nextAttr);
    let guard = 0;

    while (next && guard < 8) {
      guard += 1;
      const response = await fetch(new URL(next, location.origin).toString(), {credentials: 'same-origin'});
      if (!response.ok) break;
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector(rootSelector);
      if (!fresh) break;
      mergeIds(ids, idsFromNode(fresh.querySelector(idSelector)));
      next = nextUrlFrom(fresh, nextAttr);
    }
    return ids;
  }

  function compatibleIds(root) {
    if (!root._vdbCompatibleIdsPromise) {
      root._vdbCompatibleIdsPromise = loadPaginatedIds(
        root,
        '[data-vdb-fitment-ids]',
        'data-vdb-fitment-next-url',
        'vdb-fitment-status'
      );
    }
    return root._vdbCompatibleIdsPromise;
  }

  function summary(selection) {
    try {
      if (window.VDBVehicle && typeof window.VDBVehicle.selectionSummary === 'function') {
        const value = window.VDBVehicle.selectionSummary(selection, true);
        if (value) return value;
      }
    } catch (_) { /* fallback below */ }
    return [
      selection && selection.makeName,
      selection && selection.modelName,
      selection && selection.year,
      selection && (selection.engineShortLabel || selection.engineName || selection.engineLabel)
    ].filter(Boolean).join(' · ') || 'your selected vehicle';
  }

  function setState(root, state, title, copy, icon) {
    const box = root.querySelector('[data-vdb-fitment-box]');
    const titleNode = root.querySelector('[data-vdb-fitment-title]');
    const copyNode = root.querySelector('[data-vdb-fitment-copy]');
    const iconNode = root.querySelector('[data-vdb-fitment-icon]');
    if (!box || !titleNode || !copyNode || !iconNode) return;
    box.dataset.state = state;
    titleNode.textContent = title;
    copyNode.textContent = copy;
    iconNode.textContent = icon;
    root.dataset.vdbFitmentBuild = BUILD;
  }

  async function renderRoot(root) {
    if (!root || !root.isConnected) return;

    const shop = root.dataset.shop || defaultShop();

    // A changed Make/Model/Year/Engine is only a draft until Save vehicle is
    // pressed. Hide the previous result so it can never appear to apply to the
    // unsaved draft vehicle shown in the controls.
    if (isDraftDirty(shop)) {
      root.hidden = true;
      root.setAttribute('aria-hidden', 'true');
      root.dataset.vdbFitmentBuild = BUILD;
      return;
    }

    const selection = getSelection(shop);
    const selectedIds = selection ? selection.versionIds : [];
    const selectionKey = selectedIds.join('|');

    // Do not show a redundant fitment prompt when no vehicle has been saved.
    // The vehicle selector directly above already provides that instruction.
    if (!selectedIds.length) {
      root.hidden = true;
      root.setAttribute('aria-hidden', 'true');
      root.dataset.vdbFitmentBuild = BUILD;
      return;
    }

    root.hidden = false;
    root.removeAttribute('aria-hidden');

    const ids = await compatibleIds(root);
    const latest = getSelection(shop);
    const latestIds = latest ? latest.versionIds : [];
    if (latestIds.join('|') !== selectionKey || isDraftDirty(shop)) return;
    if (!ids.size) {
      setState(root, 'unavailable', 'Fitment data not yet available', 'Vehicle compatibility has not yet been added to this product.', 'i');
      return;
    }

    const matched = selectedIds.filter(id => ids.has(id));
    const vehicle = summary(selection);

    if (matched.length === selectedIds.length) {
      setState(root, 'fits', 'Fits your vehicle', `This part is compatible with ${vehicle}.`, '✓');
      return;
    }

    if (matched.length === 0) {
      setState(root, 'does-not-fit', 'Does not fit your vehicle', `This part is not listed as compatible with ${vehicle}.`, '×');
      return;
    }

    setState(root, 'mixed', 'More vehicle detail is required', 'This selection contains more than one underlying vehicle version and fitment differs between them. Please confirm the exact version before ordering.', '!');
  }

  function cardCompatibleIds(root) {
    return idsFromNode(root.querySelector('[data-vdb-card-fitment-ids]'));
  }

  function setCardState(root, state, label, icon) {
    const badge = root.querySelector('[data-vdb-card-fitment-badge]');
    const labelNode = root.querySelector('[data-vdb-card-fitment-label]');
    const iconNode = root.querySelector('[data-vdb-card-fitment-icon]');
    if (!badge || !labelNode || !iconNode) return;
    badge.dataset.state = state;
    labelNode.textContent = label;
    iconNode.textContent = icon;
    root.dataset.vdbFitmentBuild = BUILD;
  }

  function renderCard(root) {
    if (!root || !root.isConnected) return;
    const shop = root.dataset.shop || defaultShop();

    if (isDraftDirty(shop)) {
      root.hidden = true;
      root.setAttribute('aria-hidden', 'true');
      root.dataset.vdbFitmentBuild = BUILD;
      return;
    }

    const selection = getSelection(shop);
    const selectedIds = selection ? selection.versionIds : [];
    if (!selectedIds.length) {
      root.hidden = true;
      root.setAttribute('aria-hidden', 'true');
      root.dataset.vdbFitmentBuild = BUILD;
      return;
    }

    root.hidden = false;
    root.removeAttribute('aria-hidden');

    // On collection/search pages the Shopify storefront filter is authoritative.
    // If Compatible Vehicle Versions is present in the URL, Shopify has already
    // removed every incompatible product before Liquid renders these cards. Do
    // not second-guess that server-side result with the broader saved-selection
    // ID set (an engine choice can legitimately resolve to multiple versions).
    const url = new URL(window.location.href);
    const vehicleFilterActive = url.searchParams.has('filter.p.m.custom.compatible_vehicle_versions');
    const path = url.pathname.replace(/\/+$/, '') || '/';
    const vehicleResultsPage = /(?:^|\/)collections\/[^/]+$/.test(path) || /(?:^|\/)search$/.test(path);
    if (vehicleResultsPage && vehicleFilterActive) {
      setCardState(root, 'fits', 'Fits your vehicle', '✓');
      return;
    }

    const ids = cardCompatibleIds(root);
    if (!ids.size) {
      setCardState(root, 'unavailable', 'Fitment not yet confirmed', 'i');
      return;
    }

    const matched = selectedIds.filter(id => ids.has(id));
    if (matched.length === selectedIds.length) {
      setCardState(root, 'fits', 'Fits your vehicle', '✓');
      return;
    }

    if (matched.length === 0) {
      setCardState(root, 'does-not-fit', "Doesn't fit your vehicle", '×');
      return;
    }

    setCardState(root, 'mixed', 'Check exact vehicle version', '!');
  }

  function renderAll() {
    document.querySelectorAll('vdb-fitment-status').forEach(renderRoot);
    document.querySelectorAll('vdb-card-fitment').forEach(renderCard);
  }

  // Define the element if no earlier version has done so. The global synchroniser
  // below deliberately runs regardless, so an older cached element definition
  // cannot prevent this build from updating the visible result.
  if (!customElements.get('vdb-fitment-status')) {
    class VDBFitmentStatus extends HTMLElement {
      connectedCallback() {
        if (this._vdbConnected) return;
        this._vdbConnected = true;
        renderRoot(this);
      }
    }
    customElements.define('vdb-fitment-status', VDBFitmentStatus);
  }

  if (!customElements.get('vdb-card-fitment')) {
    class VDBCardFitment extends HTMLElement {
      connectedCallback() {
        if (this._vdbConnected) return;
        this._vdbConnected = true;
        renderCard(this);
      }
    }
    customElements.define('vdb-card-fitment', VDBCardFitment);
  }

  document.addEventListener('vdb:vehicle:change', event => {
    const shop = event.detail && event.detail.shop ? event.detail.shop : defaultShop();
    setDraftDirty(shop, false);
    queueMicrotask(renderAll);
  });
  document.addEventListener('vdb:vehicle:ready', () => queueMicrotask(renderAll));
  document.addEventListener('shopify:section:load', () => setTimeout(renderAll, 0));

  // The instant a customer changes any vehicle field, hide the existing result.
  // Otherwise a green result for the previously saved vehicle can sit beside a
  // different draft vehicle and appear to confirm fitment before it is saved.
  document.addEventListener('change', event => {
    const target = event.target;
    if (!target || !target.closest) return;
    const form = target.closest('[data-vdb-form]');
    if (!form || !target.matches('select')) return;
    setDraftDirty(shopForNode(form), true);
  }, true);

  // Capture Save vehicle as an additional guarantee. The lookup persists the new
  // selection during the same submit event; clear draft state after that handler.
  document.addEventListener('submit', event => {
    if (event.target && event.target.matches && event.target.matches('[data-vdb-form]')) {
      const shop = shopForNode(event.target);
      setTimeout(() => {
        setDraftDirty(shop, false);
        renderAll();
      }, 0);
    }
  }, true);

  window.addEventListener('pageshow', renderAll);
  window.addEventListener('storage', event => {
    if (event.key === null || (event.key && event.key.startsWith(`vdb:vehicle:${CATALOG}:`))) renderAll();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', renderAll, {once:true});
  } else {
    renderAll();
  }
  [0, 50, 250, 750, 1500].forEach(delay => setTimeout(renderAll, delay));
})();
